import { useSubscription } from '@apollo/client';

import { ActivitiesContentType } from 'global/constants';
import { LIVE_CLASSROOM_STUDENTS_SUBSCRIPTION } from './live-classroom-students.graphql';
import { useLiveClassroomStudents } from './live-classroom-students.apollo';
import type {
  GetLiveClassroomStudentsSubscription,
  GetLiveClassroomStudentsSubscriptionVariables,
} from './live-classroom-students.graphql.generated';
import { getStudentDisplayName } from 'approot/students/logic/get-student-display-name';
import {
  WS_EVENT_ACTION_ONLINE,
  WS_EVENT_ACTION_DISCONNECT,
} from 'global/constants';

type Props = {
  classId: number | null;
  contentType: ActivitiesContentType;
  contentVersion: string;
  skip?: boolean;
};

export const useLiveClassroomStudentsSubscription = ({
  classId,
  contentType,
  contentVersion,
  skip = false,
}: Props) => {
  const { setStudentOnline, setStudentOffline, addOrUpdateStudent } =
    useLiveClassroomStudents();

  const subscription = useSubscription<
    GetLiveClassroomStudentsSubscription,
    GetLiveClassroomStudentsSubscriptionVariables
  >(LIVE_CLASSROOM_STUDENTS_SUBSCRIPTION, {
    skip: !classId || skip,
    variables: {
      input: {
        classId: classId!,
        contentType,
        contentVersion,
      },
    },
    onData: ({ data: pushedUpdate }) => {
      const students = pushedUpdate.data?.getLiveClassroomStudents;

      if (students) {
        students.forEach(student => {
          const displayName = getStudentDisplayName(student);

          // Determine online status: if action is null (first load), consider online
          // Otherwise, check if action is explicitly ONLINE
          const isOnline =
            student.action === null ||
            student.action === WS_EVENT_ACTION_ONLINE;

          // Add or update the student in our state
          addOrUpdateStudent({
            studentId: student.studentId,
            firstName: student.firstName,
            lastName: student.lastName,
            emoji: student.emeji,
            displayName,
            isOnline,
          });

          // Handle the subscription data based on action
          if (student.action === WS_EVENT_ACTION_ONLINE) {
            setStudentOnline(student.studentId);
          } else if (student.action === WS_EVENT_ACTION_DISCONNECT) {
            setStudentOffline(student.studentId);
          } else if (student.action === null) {
            // First load - student is already online
            setStudentOnline(student.studentId);
          }
        });
      }
    },
  });

  return subscription;
};
